import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Users, Download, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const AthleteSOP = () => {
  const navigate = useNavigate();

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate("/dashboard/help")} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Help Center
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <Users className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Athlete Portal Guide</h1>
          </div>
          <p className="text-muted-foreground">
            Everything you need to know about submitting deals, tracking earnings, and managing your NIL activities
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Welcome Athletes!</CardTitle>
            <CardDescription>
              The Athlete Portal helps you manage your Name, Image, and Likeness (NIL) opportunities while
              staying compliant with NCAA rules and institutional policies.
            </CardDescription>
          </CardHeader>
        </Card>

        <Accordion type="single" collapsible className="space-y-4">
          <AccordionItem value="submit-deal">
            <AccordionTrigger className="text-lg font-semibold">
              How to Submit Your First NIL Deal
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Step 1: Navigate to Submit Deal</h4>
                <p className="text-muted-foreground">
                  From your Athlete Portal dashboard, click "Submit New Deal" or navigate to Deals → Submit Deal.
                </p>

                <h4 className="font-semibold">Step 2: Enter Deal Information</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Deal Title:</strong> Brief description (e.g., "Instagram Sponsorship - XYZ Brand")</li>
                  <li><strong>Brand Name:</strong> Name of company or individual you're working with</li>
                  <li><strong>Deal Type:</strong> Select category (Endorsement, Social Media, Appearance, etc.)</li>
                  <li><strong>Compensation Amount:</strong> Total value of deal in dollars</li>
                  <li><strong>Start & End Dates:</strong> Duration of agreement</li>
                  <li><strong>Deliverables:</strong> What you'll do (posts, appearances, events, etc.)</li>
                </ul>

                <h4 className="font-semibold">Step 3: Add Brand Contact Information</h4>
                <p className="text-muted-foreground">
                  Provide contact details for the brand representative. This helps compliance officers verify
                  the deal if needed.
                </p>

                <h4 className="font-semibold">Step 4: Upload Required Documents</h4>
                <p className="text-muted-foreground">
                  <strong>Required:</strong> Signed contract or agreement
                </p>
                <p className="text-muted-foreground">
                  <strong>Recommended:</strong> Payment schedule, scope of work document
                </p>

                <h4 className="font-semibold">Step 5: Submit for Review</h4>
                <p className="text-muted-foreground">
                  Click "Submit Deal" to send to compliance office. You'll receive a confirmation and can
                  track the review status in your dashboard.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="text-sm font-semibold text-blue-900">💡 Pro Tip</p>
                  <p className="text-sm text-blue-800 mt-1">
                    Submit deals at least 7 days before you start work to allow time for compliance review!
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="track-earnings">
            <AccordionTrigger className="text-lg font-semibold">
              Understanding Your Earnings Dashboard
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Dashboard Overview</h4>
                <p className="text-muted-foreground">
                  Your Athlete Portal shows:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Total Earnings:</strong> Sum of all approved deal values</li>
                  <li><strong>Active Deals:</strong> Currently running agreements</li>
                  <li><strong>Pending Deals:</strong> Awaiting compliance review</li>
                  <li><strong>Revenue Sharing:</strong> Compensation from institutional pools (CAPS)</li>
                </ul>

                <h4 className="font-semibold mt-4">Recent Deals Section</h4>
                <p className="text-muted-foreground">
                  View your most recent NIL activities, including deal status, amounts, and review progress.
                </p>

                <h4 className="font-semibold mt-4">Compensation History</h4>
                <p className="text-muted-foreground">
                  Track all payments received from revenue sharing pools, including distribution dates and amounts.
                </p>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                  <p className="text-sm font-semibold text-yellow-900">⚠️ Important</p>
                  <p className="text-sm text-yellow-800 mt-1">
                    The dashboard shows deal values, not actual payments received. Track actual payments
                    separately for tax purposes.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="documents">
            <AccordionTrigger className="text-lg font-semibold">
              Uploading Deal Contracts and Documents
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Required Documents</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Signed contract or letter of agreement</li>
                  <li>Payment terms and schedule</li>
                  <li>Scope of work or deliverables description</li>
                </ul>

                <h4 className="font-semibold mt-4">Acceptable File Formats</h4>
                <p className="text-muted-foreground">
                  PDF (preferred), DOC/DOCX, JPG/PNG (for signed documents)
                </p>

                <h4 className="font-semibold mt-4">Document Upload Process</h4>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Click "Upload Document" in deal submission form or deal details page</li>
                  <li>Select document type from dropdown</li>
                  <li>Choose file from your device</li>
                  <li>Add optional notes about the document</li>
                  <li>Click "Upload"</li>
                </ol>

                <h4 className="font-semibold mt-4">Document Security</h4>
                <p className="text-muted-foreground">
                  All uploaded documents are encrypted and only accessible to you, compliance officers,
                  and authorized athletic department staff.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="deal-flagged">
            <AccordionTrigger className="text-lg font-semibold">
              What to Do if Your Deal is Flagged
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Common Reasons for Flags</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Missing Information:</strong> Incomplete forms or missing documents</li>
                  <li><strong>Brand Conflict:</strong> Potential conflict with institutional sponsor</li>
                  <li><strong>High Value:</strong> Deals over $10,000 require additional review</li>
                  <li><strong>Prohibited Category:</strong> Brand in restricted industry</li>
                </ul>

                <h4 className="font-semibold mt-4">What to Do</h4>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Check your dashboard or email for compliance officer message</li>
                  <li>Review the specific concern or request</li>
                  <li>Provide additional information or documentation if requested</li>
                  <li>Contact compliance office if you have questions</li>
                  <li>Do NOT begin work on the deal until approved</li>
                </ol>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                  <p className="text-sm font-semibold text-red-900">🚨 Critical</p>
                  <p className="text-sm text-red-800 mt-1">
                    Never start work on a flagged or rejected deal. Doing so may violate NCAA rules and
                    jeopardize your eligibility.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="best-practices">
            <AccordionTrigger className="text-lg font-semibold">
              Best Practices for Athletes
            </AccordionTrigger>
            <AccordionContent>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li><strong>Submit Early:</strong> Allow 7+ days for compliance review before work begins</li>
                <li><strong>Keep Records:</strong> Save copies of all contracts and correspondence</li>
                <li><strong>Be Honest:</strong> Accurately report all compensation, including gifts and products</li>
                <li><strong>Ask Questions:</strong> Contact compliance office if you're unsure about anything</li>
                <li><strong>Update Deals:</strong> Report any changes to existing agreements</li>
                <li><strong>Track Payments:</strong> Monitor actual payments for tax purposes</li>
                <li><strong>Protect Your Brand:</strong> Only work with reputable companies and brands</li>
                <li><strong>Maintain Academics:</strong> NIL activities should not interfere with classes or team commitments</li>
                <li><strong>Read Contracts:</strong> Understand what you're agreeing to before signing</li>
                <li><strong>Stay Eligible:</strong> Compliance with NIL rules protects your athletic eligibility</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Card>
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
            <CardDescription>
              Contact your compliance office for assistance with NIL questions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button>
              <Download className="mr-2 h-4 w-4" />
              Download Athlete Guide PDF
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AthleteSOP;